import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { digestApi } from '@/api/digest'
import type { GenerateDigestRequest, SendDigestRequest } from '@/types'

/**
 * Hook to fetch list of dates with digest availability
 */
export function useDigestDates(startDate?: string, endDate?: string, limit?: number) {
  return useQuery({
    queryKey: ['digest-dates', startDate, endDate, limit],
    queryFn: () => digestApi.listDates(startDate, endDate, limit),
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook to fetch a specific digest by date
 */
export function useDigest(date: string | null) {
  return useQuery({
    queryKey: ['digest', date],
    queryFn: () => digestApi.getByDate(date!),
    enabled: !!date,
    staleTime: 5 * 60 * 1000, // 5 minutes - digests don't change often
  })
}

/**
 * Hook to fetch digest HTML
 */
export function useDigestHTML(date: string | null) {
  return useQuery({
    queryKey: ['digest-html', date],
    queryFn: () => digestApi.getHTML(date!),
    enabled: !!date,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to generate a new digest
 */
export function useGenerateDigest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: GenerateDigestRequest) => digestApi.generate(request),
    onSuccess: (data) => {
      // Invalidate dates list
      queryClient.invalidateQueries({ queryKey: ['digest-dates'] })
      // Update the specific digest in cache
      const dateStr = data.reference_date.split('T')[0]
      queryClient.setQueryData(['digest', dateStr], data)
    },
  })
}

/**
 * Hook to send digest to Rocket.Chat
 */
export function useSendDigest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ date, request }: { date: string; request: SendDigestRequest }) =>
      digestApi.sendToRocket(date, request),
    onSuccess: (_, { date }) => {
      // Invalidate to refresh sent status
      queryClient.invalidateQueries({ queryKey: ['digest', date] })
      queryClient.invalidateQueries({ queryKey: ['digest-dates'] })
    },
  })
}

/**
 * Hook to delete a digest
 */
export function useDeleteDigest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (date: string) => digestApi.delete(date),
    onSuccess: (_, date) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['digest', date] })
      queryClient.invalidateQueries({ queryKey: ['digest-dates'] })
    },
  })
}
